// src/permissions.ts
var PERMISSIONS = {
  CRM_UNIFIED_READ: "crm:unified:read",
  CRM_UNIFIED_MANAGE: "crm:unified:manage",
  CRM_UNIFIED_REVIEW: "crm:unified:review",
  CRM_UNIFIED_RESEARCH: "crm:unified:research",
  // Explicit tenant-approved mail audience: never assigned by seed or catalog sync.
  CRM_BUSINESS_MAIL_READ: "crm:business_mail:read",
  CRM_BUSINESS_MAIL_MANAGE: "crm:business_mail:manage",
  PLATFORM_ADMIN: "platform:admin",
  COMPANY_SETUP_MANAGE: "company_setup:manage",
  USERS_MANAGE: "users:manage",
  ROLES_MANAGE: "roles:manage",
  SETTINGS_MANAGE: "settings:manage",
  INTEGRATIONS_CONNECTIONS_READ: "integrations:connections:read",
  INTEGRATIONS_CONNECTIONS_MANAGE: "integrations:connections:manage",
  SIMULATIONS_READ: "simulations:simulations:read",
  SIMULATIONS_EXECUTE: "simulations:simulations:execute",
  AUDIT_LOGS_READ: "audit_logs:read",
  NOTIFICATIONS_READ: "notifications:notifications:read",
  NOTIFICATIONS_UPDATE: "notifications:notifications:update",
  FEATURE_FLAGS_READ: "system:feature_flags:read",
  FEATURE_FLAGS_UPDATE: "system:feature_flags:update",
  SYSTEM_AI_PROVIDERS_READ: "system:ai_providers:read",
  DASHBOARD_READ: "dashboard:command_center:read",
  DASHBOARD_EXECUTIVE_PRIORITIES_READ: "dashboard:executive_priorities:read",
  DASHBOARD_EXECUTIVE_PRIORITIES_CREATE: "dashboard:executive_priorities:create",
  DASHBOARD_EXECUTIVE_PRIORITIES_UPDATE: "dashboard:executive_priorities:update",
  DASHBOARD_EXECUTIVE_PRIORITIES_ARCHIVE: "dashboard:executive_priorities:archive",
  DASHBOARD_EXECUTIVE_PRIORITIES_COMPLETE: "dashboard:executive_priorities:complete",
  SEARCH_READ: "search:global:read",
  PROJECTS_READ: "projects:projects:read",
  PROJECTS_CREATE: "projects:projects:create",
  PROJECTS_UPDATE: "projects:projects:update",
  PROJECTS_DELETE: "projects:projects:delete",
  PROJECTS_EXECUTE: "projects:projects:execute",
  PROJECTS_EXPORT: "projects:projects:export",
  REPORTS_EXPORT: "reports:reports:export",
  /**
   * P2 report library.
   *
   * Split into two keys because previewing and generating are different acts.
   * `read` opens the library and renders a report on screen; `execute` produces
   * the PDF, writes it to storage as a tenant document, and leaves an audited
   * run on the ledger.
   *
   * Neither rides the platform-wide read grant — see the `reports` exclusion in
   * `prisma/seed.ts`. An owner's monthly summary is the company's contract
   * position, its margin, and every customer that owes it money on one page;
   * that is not the same class of fact as a drawing measurement, and it is not
   * granted to everyone with a badge.
   */
  REPORTS_LIBRARY_READ: "reports:report_library:read",
  REPORTS_LIBRARY_EXECUTE: "reports:report_library:execute",
  FINANCE_BUDGETS_READ: "finance:project_budgets:read",
  FINANCE_AR_INVOICES_READ: "finance:ar_invoices:read",
  FINANCE_AP_INVOICES_READ: "finance:ap_invoices:read",
  FINANCE_INVOICE_COMMUNICATIONS_MANAGE: "finance:invoice_communications:manage",
  FINANCE_WIP_SNAPSHOTS_READ: "finance:wip_snapshots:read",
  FINANCE_WIP_SNAPSHOTS_CREATE: "finance:wip_snapshots:create",
  FINANCE_WIP_SNAPSHOTS_UPDATE: "finance:wip_snapshots:update",
  FINANCE_WIP_SNAPSHOTS_EXECUTE: "finance:wip_snapshots:execute",
  FINANCE_CASH_FLOW_FORECASTS_READ: "finance:cash_flow_forecasts:read",
  FINANCE_CASH_FLOW_FORECASTS_CREATE: "finance:cash_flow_forecasts:create",
  FINANCE_CASH_FLOW_FORECASTS_UPDATE: "finance:cash_flow_forecasts:update",
  FINANCE_CASH_FLOW_FORECASTS_EXECUTE: "finance:cash_flow_forecasts:execute",
  FINANCE_CASH_FLOW_FORECASTS_DELETE: "finance:cash_flow_forecasts:delete",
  FINANCE_CASH_FLOW_FORECASTS_EXPORT: "finance:cash_flow_forecasts:export",
  FINANCE_BUDGETS_CREATE: "finance:project_budgets:create",
  FINANCE_BUDGETS_UPDATE: "finance:project_budgets:update",
  FINANCE_BUDGETS_DELETE: "finance:project_budgets:delete",
  FINANCE_BUDGETS_EXECUTE: "finance:project_budgets:execute",
  FINANCE_BUDGETS_EXPORT: "finance:project_budgets:export",
  FINANCE_BUDGET_LINE_ITEMS_READ: "finance:budget_line_items:read",
  FINANCE_BUDGET_LINE_ITEMS_CREATE: "finance:budget_line_items:create",
  FINANCE_BUDGET_LINE_ITEMS_UPDATE: "finance:budget_line_items:update",
  FINANCE_BUDGET_LINE_ITEMS_DELETE: "finance:budget_line_items:delete",
  FINANCE_BUDGET_LINE_ITEMS_EXECUTE: "finance:budget_line_items:execute",
  FINANCE_BUDGET_LINE_ITEMS_EXPORT: "finance:budget_line_items:export",
  FINANCE_COMMITMENTS_READ: "finance:commitments:read",
  FINANCE_COMMITMENTS_CREATE: "finance:commitments:create",
  FINANCE_COMMITMENTS_UPDATE: "finance:commitments:update",
  FINANCE_COMMITMENTS_DELETE: "finance:commitments:delete",
  FINANCE_COMMITMENTS_EXECUTE: "finance:commitments:execute",
  FINANCE_COMMITMENTS_EXPORT: "finance:commitments:export",
  FINANCE_ACTUAL_COSTS_READ: "finance:actual_costs:read",
  FINANCE_ACTUAL_COSTS_CREATE: "finance:actual_costs:create",
  FINANCE_ACTUAL_COSTS_UPDATE: "finance:actual_costs:update",
  FINANCE_ACTUAL_COSTS_DELETE: "finance:actual_costs:delete",
  FINANCE_ACTUAL_COSTS_EXECUTE: "finance:actual_costs:execute",
  FINANCE_ACTUAL_COSTS_EXPORT: "finance:actual_costs:export",
  FINANCE_FORECAST_ITEMS_READ: "finance:forecast_items:read",
  FINANCE_FORECAST_ITEMS_CREATE: "finance:forecast_items:create",
  FINANCE_FORECAST_ITEMS_UPDATE: "finance:forecast_items:update",
  FINANCE_FORECAST_ITEMS_DELETE: "finance:forecast_items:delete",
  FINANCE_FORECAST_ITEMS_EXECUTE: "finance:forecast_items:execute",
  FINANCE_FORECAST_ITEMS_EXPORT: "finance:forecast_items:export",
  FINANCE_COST_CODES_READ: "finance:cost_codes:read",
  FINANCE_COST_CODES_CREATE: "finance:cost_codes:create",
  FINANCE_COST_CODES_UPDATE: "finance:cost_codes:update",
  FINANCE_COST_CODES_DELETE: "finance:cost_codes:delete",
  FINANCE_COST_CODES_EXECUTE: "finance:cost_codes:execute",
  FINANCE_COST_CODES_EXPORT: "finance:cost_codes:export",
  FINANCE_AP_INVOICES_EXECUTE: "finance:ap_invoices:execute",
  // M06 AIA billing: G702/G703 pay applications, retainage, financial periods.
  FINANCE_BILLING_APPLICATIONS_READ: "finance:billing_applications:read",
  FINANCE_BILLING_APPLICATIONS_CREATE: "finance:billing_applications:create",
  FINANCE_BILLING_APPLICATIONS_UPDATE: "finance:billing_applications:update",
  FINANCE_BILLING_APPLICATIONS_DELETE: "finance:billing_applications:delete",
  FINANCE_BILLING_APPLICATIONS_EXECUTE: "finance:billing_applications:execute",
  FINANCE_BILLING_APPLICATIONS_EXPORT: "finance:billing_applications:export",
  FINANCE_BILLING_APPLICATION_LINES_READ: "finance:billing_application_lines:read",
  FINANCE_BILLING_APPLICATION_LINES_CREATE: "finance:billing_application_lines:create",
  FINANCE_BILLING_APPLICATION_LINES_UPDATE: "finance:billing_application_lines:update",
  FINANCE_BILLING_APPLICATION_LINES_DELETE: "finance:billing_application_lines:delete",
  FINANCE_FINANCIAL_PERIODS_READ: "finance:financial_periods:read",
  FINANCE_FINANCIAL_PERIODS_CREATE: "finance:financial_periods:create",
  FINANCE_FINANCIAL_PERIODS_UPDATE: "finance:financial_periods:update",
  FINANCE_FINANCIAL_PERIODS_EXECUTE: "finance:financial_periods:execute",
  FINANCE_RETAINAGE_RECORDS_READ: "finance:retainage_records:read",
  FINANCE_RETAINAGE_RECORDS_EXECUTE: "finance:retainage_records:execute",
  FINANCE_RETAINAGE_RECORDS_CREATE: "finance:retainage_records:create",
  FINANCE_RETAINAGE_RECORDS_UPDATE: "finance:retainage_records:update",
  FINANCE_RETAINAGE_RECORDS_DELETE: "finance:retainage_records:delete",
  FINANCE_RETAINAGE_RECORDS_EXPORT: "finance:retainage_records:export",
  PROCUREMENT_VENDORS_READ: "procurement:vendors:read",
  PROCUREMENT_VENDORS_CREATE: "procurement:vendors:create",
  PROCUREMENT_VENDORS_UPDATE: "procurement:vendors:update",
  PROCUREMENT_PURCHASE_ORDERS_READ: "procurement:purchase_orders:read",
  PROCUREMENT_PURCHASE_ORDERS_CREATE: "procurement:purchase_orders:create",
  PROCUREMENT_PURCHASE_ORDERS_UPDATE: "procurement:purchase_orders:update",
  // Deliberately separate from UPDATE: the maker (drafts/submits a PO) must
  // not also be the checker (approves it) by default. See docs/implementation_decisions.md.
  PROCUREMENT_PURCHASE_ORDERS_APPROVE: "procurement:purchase_orders:approve",
  FINANCE_LEDGER_ACCOUNTS_READ: "finance:ledger_accounts:read",
  FINANCE_LEDGER_ACCOUNTS_CREATE: "finance:ledger_accounts:create",
  FINANCE_LEDGER_ACCOUNTS_UPDATE: "finance:ledger_accounts:update",
  FINANCE_JOURNAL_ENTRIES_READ: "finance:journal_entries:read",
  FINANCE_JOURNAL_ENTRIES_CREATE: "finance:journal_entries:create",
  // POST is a distinct, harder-to-reverse action from CREATE (draft): posting
  // locks the entry into the trial balance and requires its own grant.
  FINANCE_JOURNAL_ENTRIES_POST: "finance:journal_entries:post",
  LEGAL_ENTITIES_READ: "legal:entities:read",
  LEGAL_ENTITIES_CREATE: "legal:entities:create",
  LEGAL_ENTITIES_UPDATE: "legal:entities:update",
  // Archive is its own grant: an entity with filings, owners and agreements
  // behind it is not something an editor should be able to retire by default.
  LEGAL_ENTITIES_DELETE: "legal:entities:delete",
  // The EIN. Held encrypted with a last-4; a full reveal is a separate, audited
  // read and setting it is a separate write — never implied by entities:read/update.
  LEGAL_ENTITIES_SENSITIVE_READ: "legal:entities_sensitive:read",
  LEGAL_ENTITIES_SENSITIVE_UPDATE: "legal:entities_sensitive:update",
  /**
   * P8 Legal — the internal legal back office (CorporateOS parity).
   * `matters` covers matters, their deadlines, assigned counsel and matter
   * documents; `counsel` covers law firms, attorneys, retainer ledgers and
   * counsel documents; `expenses` is the legal invoice/expense ledger.
   */
  LEGAL_MATTERS_READ: "legal:matters:read",
  LEGAL_MATTERS_CREATE: "legal:matters:create",
  LEGAL_MATTERS_UPDATE: "legal:matters:update",
  LEGAL_MATTERS_DELETE: "legal:matters:delete",
  LEGAL_COUNSEL_READ: "legal:counsel:read",
  LEGAL_COUNSEL_CREATE: "legal:counsel:create",
  LEGAL_COUNSEL_UPDATE: "legal:counsel:update",
  LEGAL_COUNSEL_DELETE: "legal:counsel:delete",
  LEGAL_EXPENSES_READ: "legal:expenses:read",
  LEGAL_EXPENSES_CREATE: "legal:expenses:create",
  LEGAL_EXPENSES_UPDATE: "legal:expenses:update",
  LEGAL_EXPENSES_DELETE: "legal:expenses:delete",
  BENEFITS_PLANS_READ: "benefits:plans:read",
  BENEFITS_PLANS_CREATE: "benefits:plans:create",
  BENEFITS_PLANS_UPDATE: "benefits:plans:update",
  BENEFITS_PLANS_DELETE: "benefits:plans:delete",
  // Plan contacts, documents and written policies ride `benefits:plans:*`.
  // Claims and per-employee enrollments are their own resources: a claim is
  // money owed and an enrollment is a person's election.
  BENEFITS_CLAIMS_READ: "benefits:claims:read",
  BENEFITS_CLAIMS_CREATE: "benefits:claims:create",
  BENEFITS_CLAIMS_UPDATE: "benefits:claims:update",
  BENEFITS_CLAIMS_DELETE: "benefits:claims:delete",
  BENEFITS_ENROLLMENTS_READ: "benefits:enrollments:read",
  BENEFITS_ENROLLMENTS_CREATE: "benefits:enrollments:create",
  BENEFITS_ENROLLMENTS_UPDATE: "benefits:enrollments:update",
  BENEFITS_ENROLLMENTS_DELETE: "benefits:enrollments:delete",
  CONTRACTS_READ: "contracts:contracts:read",
  CONTRACTS_CREATE: "contracts:contracts:create",
  CONTRACTS_UPDATE: "contracts:contracts:update",
  SCHEDULING_SCHEDULES_READ: "scheduling:schedules:read",
  SCHEDULING_SCHEDULES_CREATE: "scheduling:schedules:create",
  SCHEDULING_SCHEDULES_UPDATE: "scheduling:schedules:update",
  SCHEDULING_SCHEDULES_DELETE: "scheduling:schedules:delete",
  SCHEDULING_SCHEDULES_EXECUTE: "scheduling:schedules:execute",
  SCHEDULING_SCHEDULES_EXPORT: "scheduling:schedules:export",
  SCHEDULING_SCHEDULE_VERSIONS_READ: "scheduling:schedule_versions:read",
  SCHEDULING_SCHEDULE_VERSIONS_CREATE: "scheduling:schedule_versions:create",
  SCHEDULING_SCHEDULE_VERSIONS_UPDATE: "scheduling:schedule_versions:update",
  SCHEDULING_SCHEDULE_VERSIONS_DELETE: "scheduling:schedule_versions:delete",
  SCHEDULING_SCHEDULE_VERSIONS_EXECUTE: "scheduling:schedule_versions:execute",
  SCHEDULING_SCHEDULE_VERSIONS_EXPORT: "scheduling:schedule_versions:export",
  SCHEDULING_SCHEDULE_TASKS_READ: "scheduling:schedule_tasks:read",
  SCHEDULING_SCHEDULE_TASKS_CREATE: "scheduling:schedule_tasks:create",
  SCHEDULING_SCHEDULE_TASKS_UPDATE: "scheduling:schedule_tasks:update",
  SCHEDULING_SCHEDULE_TASKS_DELETE: "scheduling:schedule_tasks:delete",
  SCHEDULING_SCHEDULE_TASKS_EXECUTE: "scheduling:schedule_tasks:execute",
  SCHEDULING_SCHEDULE_TASKS_EXPORT: "scheduling:schedule_tasks:export",
  SCHEDULING_SCHEDULE_DEPENDENCIES_READ: "scheduling:schedule_dependencies:read",
  SCHEDULING_SCHEDULE_DEPENDENCIES_CREATE: "scheduling:schedule_dependencies:create",
  SCHEDULING_SCHEDULE_DEPENDENCIES_UPDATE: "scheduling:schedule_dependencies:update",
  SCHEDULING_SCHEDULE_DEPENDENCIES_DELETE: "scheduling:schedule_dependencies:delete",
  SCHEDULING_SCHEDULE_DEPENDENCIES_EXECUTE: "scheduling:schedule_dependencies:execute",
  SCHEDULING_SCHEDULE_DEPENDENCIES_EXPORT: "scheduling:schedule_dependencies:export",
  SCHEDULING_MILESTONES_READ: "scheduling:milestones:read",
  SCHEDULING_MILESTONES_CREATE: "scheduling:milestones:create",
  SCHEDULING_MILESTONES_UPDATE: "scheduling:milestones:update",
  SCHEDULING_MILESTONES_DELETE: "scheduling:milestones:delete",
  SCHEDULING_MILESTONES_EXECUTE: "scheduling:milestones:execute",
  SCHEDULING_MILESTONES_EXPORT: "scheduling:milestones:export",
  SCHEDULING_LOOKAHEAD_PLANS_READ: "scheduling:lookahead_plans:read",
  SCHEDULING_LOOKAHEAD_PLANS_CREATE: "scheduling:lookahead_plans:create",
  SCHEDULING_LOOKAHEAD_PLANS_UPDATE: "scheduling:lookahead_plans:update",
  SCHEDULING_LOOKAHEAD_PLANS_DELETE: "scheduling:lookahead_plans:delete",
  SCHEDULING_LOOKAHEAD_PLANS_EXECUTE: "scheduling:lookahead_plans:execute",
  SCHEDULING_LOOKAHEAD_PLANS_EXPORT: "scheduling:lookahead_plans:export",
  SCHEDULING_CONSTRAINT_LOGS_READ: "scheduling:constraint_logs:read",
  SCHEDULING_CONSTRAINT_LOGS_CREATE: "scheduling:constraint_logs:create",
  SCHEDULING_CONSTRAINT_LOGS_UPDATE: "scheduling:constraint_logs:update",
  SCHEDULING_CONSTRAINT_LOGS_DELETE: "scheduling:constraint_logs:delete",
  SCHEDULING_CONSTRAINT_LOGS_EXECUTE: "scheduling:constraint_logs:execute",
  SCHEDULING_CONSTRAINT_LOGS_EXPORT: "scheduling:constraint_logs:export",
  DOCUMENTS_RFIS_READ: "documents:rfis:read",
  DOCUMENTS_RFIS_CREATE: "documents:rfis:create",
  DOCUMENTS_RFIS_UPDATE: "documents:rfis:update",
  DOCUMENTS_RFIS_DELETE: "documents:rfis:delete",
  DOCUMENTS_RFIS_EXECUTE: "documents:rfis:execute",
  DOCUMENTS_RFIS_EXPORT: "documents:rfis:export",
  DOCUMENTS_RFI_RESPONSES_READ: "documents:rfi_responses:read",
  DOCUMENTS_RFI_RESPONSES_CREATE: "documents:rfi_responses:create",
  DOCUMENTS_RFI_RESPONSES_UPDATE: "documents:rfi_responses:update",
  DOCUMENTS_RFI_RESPONSES_DELETE: "documents:rfi_responses:delete",
  DOCUMENTS_RFI_RESPONSES_EXECUTE: "documents:rfi_responses:execute",
  DOCUMENTS_RFI_RESPONSES_EXPORT: "documents:rfi_responses:export",
  DOCUMENTS_SUBMITTALS_READ: "documents:submittals:read",
  DOCUMENTS_SUBMITTALS_CREATE: "documents:submittals:create",
  DOCUMENTS_SUBMITTALS_UPDATE: "documents:submittals:update",
  DOCUMENTS_SUBMITTALS_DELETE: "documents:submittals:delete",
  DOCUMENTS_SUBMITTALS_EXECUTE: "documents:submittals:execute",
  DOCUMENTS_SUBMITTALS_EXPORT: "documents:submittals:export",
  DOCUMENTS_SUBMITTAL_REVIEWS_READ: "documents:submittal_reviews:read",
  DOCUMENTS_SUBMITTAL_REVIEWS_CREATE: "documents:submittal_reviews:create",
  DOCUMENTS_SUBMITTAL_REVIEWS_UPDATE: "documents:submittal_reviews:update",
  DOCUMENTS_SUBMITTAL_REVIEWS_DELETE: "documents:submittal_reviews:delete",
  DOCUMENTS_SUBMITTAL_REVIEWS_EXECUTE: "documents:submittal_reviews:execute",
  DOCUMENTS_SUBMITTAL_REVIEWS_EXPORT: "documents:submittal_reviews:export",
  DOCUMENTS_CHANGE_ORDERS_READ: "documents:change_orders:read",
  DOCUMENTS_CHANGE_ORDERS_CREATE: "documents:change_orders:create",
  DOCUMENTS_CHANGE_ORDERS_UPDATE: "documents:change_orders:update",
  DOCUMENTS_CHANGE_ORDERS_DELETE: "documents:change_orders:delete",
  DOCUMENTS_CHANGE_ORDERS_EXECUTE: "documents:change_orders:execute",
  DOCUMENTS_CHANGE_ORDERS_EXPORT: "documents:change_orders:export",
  DOCUMENTS_CHANGE_ORDER_ITEMS_READ: "documents:change_order_items:read",
  DOCUMENTS_CHANGE_ORDER_ITEMS_CREATE: "documents:change_order_items:create",
  DOCUMENTS_CHANGE_ORDER_ITEMS_UPDATE: "documents:change_order_items:update",
  DOCUMENTS_CHANGE_ORDER_ITEMS_DELETE: "documents:change_order_items:delete",
  DOCUMENTS_CHANGE_ORDER_ITEMS_EXECUTE: "documents:change_order_items:execute",
  DOCUMENTS_CHANGE_ORDER_ITEMS_EXPORT: "documents:change_order_items:export",
  // M07 change-order package: cover letter + backup sheet + send + response.
  DOCUMENTS_CHANGE_ORDER_PACKAGES_READ: "documents:change_order_packages:read",
  DOCUMENTS_CHANGE_ORDER_PACKAGES_CREATE: "documents:change_order_packages:create",
  DOCUMENTS_CHANGE_ORDER_PACKAGES_UPDATE: "documents:change_order_packages:update",
  DOCUMENTS_CHANGE_ORDER_PACKAGES_DELETE: "documents:change_order_packages:delete",
  DOCUMENTS_CHANGE_ORDER_PACKAGES_EXECUTE: "documents:change_order_packages:execute",
  DOCUMENTS_ASSETS_READ: "documents:assets:read",
  DOCUMENTS_ASSETS_CREATE: "documents:assets:create",
  DOCUMENTS_ASSETS_UPDATE: "documents:assets:update",
  DOCUMENTS_ASSETS_DELETE: "documents:assets:delete",
  DOCUMENTS_ASSETS_EXECUTE: "documents:assets:execute",
  DOCUMENTS_ASSETS_EXPORT: "documents:assets:export",
  DOCUMENTS_FOLDERS_READ: "documents:folders:read",
  DOCUMENTS_FOLDERS_CREATE: "documents:folders:create",
  DOCUMENTS_FOLDERS_UPDATE: "documents:folders:update",
  DOCUMENTS_FOLDERS_DELETE: "documents:folders:delete",
  DIGITAL_TWINS_BIM_MODELS_READ: "digital_twins:bim_models:read",
  DIGITAL_TWINS_BIM_MODELS_CREATE: "digital_twins:bim_models:create",
  DIGITAL_TWINS_BIM_MODELS_UPDATE: "digital_twins:bim_models:update",
  DIGITAL_TWINS_BIM_MODELS_DELETE: "digital_twins:bim_models:delete",
  DIGITAL_TWINS_MODEL_ZONES_READ: "digital_twins:model_zones:read",
  DIGITAL_TWINS_MODEL_ZONES_CREATE: "digital_twins:model_zones:create",
  DIGITAL_TWINS_MODEL_ZONES_UPDATE: "digital_twins:model_zones:update",
  DIGITAL_TWINS_MODEL_ZONES_DELETE: "digital_twins:model_zones:delete",
  DIGITAL_TWINS_ISSUE_MARKERS_READ: "digital_twins:issue_markers:read",
  DIGITAL_TWINS_ISSUE_MARKERS_CREATE: "digital_twins:issue_markers:create",
  DIGITAL_TWINS_ISSUE_MARKERS_UPDATE: "digital_twins:issue_markers:update",
  DIGITAL_TWINS_ISSUE_MARKERS_DELETE: "digital_twins:issue_markers:delete",
  DIGITAL_TWINS_OVERLAY_LAYERS_READ: "digital_twins:overlay_layers:read",
  DIGITAL_TWINS_OVERLAY_LAYERS_CREATE: "digital_twins:overlay_layers:create",
  DIGITAL_TWINS_OVERLAY_LAYERS_UPDATE: "digital_twins:overlay_layers:update",
  DIGITAL_TWINS_OVERLAY_LAYERS_DELETE: "digital_twins:overlay_layers:delete",
  DIGITAL_TWINS_DRONE_SCANS_READ: "digital_twins:drone_scans:read",
  DIGITAL_TWINS_DRONE_SCANS_CREATE: "digital_twins:drone_scans:create",
  DIGITAL_TWINS_DRONE_SCANS_UPDATE: "digital_twins:drone_scans:update",
  DIGITAL_TWINS_DRONE_SCANS_DELETE: "digital_twins:drone_scans:delete",
  DIGITAL_TWINS_SENSOR_DEVICES_READ: "digital_twins:sensor_devices:read",
  DIGITAL_TWINS_SENSOR_DEVICES_CREATE: "digital_twins:sensor_devices:create",
  DIGITAL_TWINS_SENSOR_DEVICES_UPDATE: "digital_twins:sensor_devices:update",
  DIGITAL_TWINS_SENSOR_DEVICES_DELETE: "digital_twins:sensor_devices:delete",
  DIGITAL_TWINS_SENSOR_READINGS_READ: "digital_twins:sensor_readings:read",
  DIGITAL_TWINS_SENSOR_READINGS_CREATE: "digital_twins:sensor_readings:create",
  DIGITAL_TWINS_SENSOR_READINGS_UPDATE: "digital_twins:sensor_readings:update",
  DIGITAL_TWINS_SENSOR_READINGS_DELETE: "digital_twins:sensor_readings:delete",
  DIGITAL_TWINS_PROGRESS_SNAPSHOTS_READ: "digital_twins:progress_snapshots:read",
  DIGITAL_TWINS_PROGRESS_SNAPSHOTS_CREATE: "digital_twins:progress_snapshots:create",
  DIGITAL_TWINS_PROGRESS_SNAPSHOTS_UPDATE: "digital_twins:progress_snapshots:update",
  DIGITAL_TWINS_PROGRESS_SNAPSHOTS_DELETE: "digital_twins:progress_snapshots:delete",
  DIGITAL_TWINS_TWIN_SESSIONS_READ: "digital_twins:twin_sessions:read",
  DIGITAL_TWINS_TWIN_SESSIONS_CREATE: "digital_twins:twin_sessions:create",
  DIGITAL_TWINS_TWIN_SESSIONS_UPDATE: "digital_twins:twin_sessions:update",
  DIGITAL_TWINS_TWIN_SESSIONS_DELETE: "digital_twins:twin_sessions:delete",
  DIGITAL_TWINS_TWIN_SESSIONS_EXECUTE: "digital_twins:twin_sessions:execute",
  DIGITAL_TWINS_TWIN_COMPONENTS_READ: "digital_twins:twin_components:read",
  DIGITAL_TWINS_TWIN_OVERLAYS_READ: "digital_twins:twin_overlays:read",
  DIGITAL_TWINS_TWIN_OVERLAYS_EXECUTE: "digital_twins:twin_overlays:execute",
  DIGITAL_TWINS_TWIN_COMMANDS_EXECUTE: "digital_twins:twin_commands:execute",
  DIGITAL_TWINS_TWIN_SAVED_VIEWS_READ: "digital_twins:twin_saved_views:read",
  DIGITAL_TWINS_TWIN_SAVED_VIEWS_CREATE: "digital_twins:twin_saved_views:create",
  DIGITAL_TWINS_TWIN_SAVED_VIEWS_UPDATE: "digital_twins:twin_saved_views:update",
  DIGITAL_TWINS_TWIN_SAVED_VIEWS_DELETE: "digital_twins:twin_saved_views:delete",
  SAFETY_INCIDENTS_READ: "safety:safety_incidents:read",
  QUALITY_INSPECTIONS_READ: "quality:quality_inspections:read",
  // M08 field operations. `approve` is deliberately its own key rather than
  // part of `execute`: a field worker holds create/update so they can file
  // today's report, and must not be able to certify it themselves.
  FIELD_DAILY_REPORTS_READ: "field:daily_reports:read",
  FIELD_DAILY_REPORTS_CREATE: "field:daily_reports:create",
  FIELD_DAILY_REPORTS_UPDATE: "field:daily_reports:update",
  FIELD_DAILY_REPORTS_DELETE: "field:daily_reports:delete",
  FIELD_DAILY_REPORTS_EXECUTE: "field:daily_reports:execute",
  FIELD_DAILY_REPORTS_EXPORT: "field:daily_reports:export",
  FIELD_DAILY_REPORTS_APPROVE: "field:daily_reports:approve",
  FIELD_OBSERVATIONS_READ: "field:observations:read",
  FIELD_OBSERVATIONS_CREATE: "field:observations:create",
  FIELD_OBSERVATIONS_UPDATE: "field:observations:update",
  FIELD_OBSERVATIONS_DELETE: "field:observations:delete",
  FIELD_OBSERVATIONS_EXECUTE: "field:observations:execute",
  FIELD_FIELD_TASKS_READ: "field:field_tasks:read",
  FIELD_FIELD_TASKS_CREATE: "field:field_tasks:create",
  FIELD_FIELD_TASKS_UPDATE: "field:field_tasks:update",
  FIELD_FIELD_TASKS_DELETE: "field:field_tasks:delete",
  FIELD_FIELD_TASKS_EXECUTE: "field:field_tasks:execute",
  FIELD_DISPATCHES_READ: "field:dispatches:read",
  FIELD_DISPATCHES_CREATE: "field:dispatches:create",
  FIELD_DISPATCHES_UPDATE: "field:dispatches:update",
  FIELD_DISPATCHES_DELETE: "field:dispatches:delete",
  FIELD_DISPATCHES_EXECUTE: "field:dispatches:execute",
  FIELD_PRODUCTION_QUANTITIES_READ: "field:production_quantities:read",
  FIELD_PRODUCTION_QUANTITIES_CREATE: "field:production_quantities:create",
  FIELD_PRODUCTION_QUANTITIES_UPDATE: "field:production_quantities:update",
  FIELD_PRODUCTION_QUANTITIES_DELETE: "field:production_quantities:delete",
  FIELD_CAMERA_FEEDS_READ: "field:camera_feeds:read",
  FIELD_CAMERA_FEEDS_CREATE: "field:camera_feeds:create",
  FIELD_CAMERA_FEEDS_UPDATE: "field:camera_feeds:update",
  FIELD_CAMERA_FEEDS_DELETE: "field:camera_feeds:delete",
  // M09 sub compliance. Certificates finally get a controller of their own;
  // `compliance:vendor_profiles:update` is a narrow sub-resource of Vendor — the
  // contact, W-9, licence, and prequalification fields compliance owns — and is
  // deliberately not `procurement:vendors:update`, which would hand the whole
  // vendor record to anyone who can chase a certificate.
  COMPLIANCE_INSURANCE_CERTIFICATES_READ: "compliance:insurance_certificates:read",
  COMPLIANCE_INSURANCE_CERTIFICATES_CREATE: "compliance:insurance_certificates:create",
  COMPLIANCE_INSURANCE_CERTIFICATES_UPDATE: "compliance:insurance_certificates:update",
  COMPLIANCE_INSURANCE_CERTIFICATES_DELETE: "compliance:insurance_certificates:delete",
  COMPLIANCE_INSURANCE_CERTIFICATES_EXECUTE: "compliance:insurance_certificates:execute",
  COMPLIANCE_INSURANCE_CERTIFICATES_EXPORT: "compliance:insurance_certificates:export",
  COMPLIANCE_CHASE_TASKS_READ: "compliance:chase_tasks:read",
  COMPLIANCE_CHASE_TASKS_CREATE: "compliance:chase_tasks:create",
  COMPLIANCE_CHASE_TASKS_UPDATE: "compliance:chase_tasks:update",
  COMPLIANCE_CHASE_TASKS_EXECUTE: "compliance:chase_tasks:execute",
  COMPLIANCE_VENDOR_PROFILES_READ: "compliance:vendor_profiles:read",
  COMPLIANCE_VENDOR_PROFILES_UPDATE: "compliance:vendor_profiles:update",
  // M10 inbox intelligence. There is deliberately no `create` or `delete` on a
  // thread: threads are created by ingestion and are never removed, because a
  // deleted thread is indistinguishable from a message the platform never saw.
  // `inbox:threads:execute` is the triage verb (file / ignore / reopen / link);
  // `inbox:drafts:execute` is generate and send — the key that can put words in
  // the company's name into someone else's mailbox.
  INBOX_THREADS_READ: "inbox:threads:read",
  INBOX_THREADS_UPDATE: "inbox:threads:update",
  INBOX_THREADS_EXECUTE: "inbox:threads:execute",
  INBOX_MESSAGES_READ: "inbox:messages:read",
  INBOX_DRAFTS_READ: "inbox:drafts:read",
  INBOX_DRAFTS_CREATE: "inbox:drafts:create",
  INBOX_DRAFTS_UPDATE: "inbox:drafts:update",
  INBOX_DRAFTS_EXECUTE: "inbox:drafts:execute",
  INBOX_MAILBOXES_READ: "inbox:mailboxes:read",
  INBOX_MAILBOXES_EXECUTE: "inbox:mailboxes:execute",
  /**
   * Department mailbox → COS module routing (Graph ingest foundation).
   * `read` lists routes and health. `manage` creates, edits, disables,
   * tests, and resubscribes. Admin-gated on Integrations.
   */
  MAILBOX_ROUTES_READ: "mailbox_routes:read",
  MAILBOX_ROUTES_MANAGE: "mailbox_routes:manage",
  /**
   * AP bill intake ("Incoming bills": bills/remittances captured from the
   * AP/AR mailbox routes and cross-checked against QuickBooks). `read` lists
   * them with their match status; `review` confirms, dismisses, links a vendor
   * and re-runs extraction/matching; `backfill:execute` queues historical
   * messages from any tenant mailbox through the same pipeline (admin only).
   */
  BILL_INTAKE_BILLS_READ: "bill_intake:bills:read",
  BILL_INTAKE_BILLS_REVIEW: "bill_intake:bills:review",
  BILL_INTAKE_BACKFILL_EXECUTE: "bill_intake:backfill:execute",
  AI_AGENTS_READ: "ai:agents:read",
  AI_AGENTS_UPDATE: "ai:agents:update",
  AI_AGENTS_EXECUTE: "ai:agents:execute",
  AI_TASKS_READ: "ai:tasks:read",
  AI_TASKS_CREATE: "ai:tasks:create",
  AI_TASKS_EXECUTE: "ai:tasks:execute",
  AI_RUNS_READ: "ai:runs:read",
  AI_RUNS_EXECUTE: "ai:runs:execute",
  AI_RECOMMENDATIONS_READ: "ai:recommendations:read",
  AI_RECOMMENDATIONS_APPROVE: "ai:recommendations:approve",
  AI_LOGS_READ: "ai:logs:read",
  AI_CHAT_READ: "ai:chat:read",
  AI_CHAT_WRITE: "ai:chat:write",
  /**
   * Cross-module Approvals hub. `read` opens the queue and audit trail.
   * `create` files a request. `decide` is the server-enforced approve/reject
   * gate — never inferred from the client.
   */
  APPROVALS_READ: "approvals:read",
  APPROVALS_CREATE: "approvals:create",
  APPROVALS_DECIDE: "approvals:decide",
  /**
   * Per-user Microsoft 365 Executive Workspace (delegated OAuth).
   * `m365:workspace:read` opens Mail/Calendar/Tasks/Notes/Files.
   * `m365:workspace:write` covers send/reply/mutate on those surfaces.
   * `m365:connection:manage` is connect/disconnect for the signed-in user.
   */
  M365_WORKSPACE_READ: "m365:workspace:read",
  M365_WORKSPACE_WRITE: "m365:workspace:write",
  M365_CONNECTION_MANAGE: "m365:connection:manage",
  CRM_CLIENT_ACCOUNTS_READ: "crm:client_accounts:read",
  CRM_CLIENT_ACCOUNTS_CREATE: "crm:client_accounts:create",
  CRM_CLIENT_ACCOUNTS_UPDATE: "crm:client_accounts:update",
  CRM_CLIENT_ACCOUNTS_DELETE: "crm:client_accounts:delete",
  CRM_CLIENT_ACCOUNTS_EXECUTE: "crm:client_accounts:execute",
  CRM_CLIENT_ACCOUNTS_EXPORT: "crm:client_accounts:export",
  CRM_CLIENT_CONTACTS_READ: "crm:client_contacts:read",
  CRM_CLIENT_CONTACTS_CREATE: "crm:client_contacts:create",
  CRM_CLIENT_CONTACTS_UPDATE: "crm:client_contacts:update",
  CRM_CLIENT_CONTACTS_DELETE: "crm:client_contacts:delete",
  ESTIMATING_LEADS_READ: "estimating:leads:read",
  ESTIMATING_LEADS_CREATE: "estimating:leads:create",
  ESTIMATING_LEADS_UPDATE: "estimating:leads:update",
  ESTIMATING_LEADS_DELETE: "estimating:leads:delete",
  ESTIMATING_LEADS_EXECUTE: "estimating:leads:execute",
  ESTIMATING_LEADS_EXPORT: "estimating:leads:export",
  ESTIMATING_CRITERIA_PROFILES_READ: "estimating:criteria_profiles:read",
  ESTIMATING_CRITERIA_PROFILES_UPDATE: "estimating:criteria_profiles:update",
  ESTIMATING_SETTINGS_READ: "estimating:settings:read",
  ESTIMATING_COMPANY_COSTS_READ: "estimating:company_costs:read",
  ESTIMATING_COMPANY_COSTS_CREATE: "estimating:company_costs:create",
  ESTIMATING_COMPANY_COSTS_UPDATE: "estimating:company_costs:update",
  ESTIMATING_COMPANY_COSTS_APPROVE: "estimating:company_costs:approve",
  ESTIMATING_SETTINGS_UPDATE: "estimating:settings:update",
  ESTIMATING_EQUIPMENT_PLANS_READ: "estimating:equipment_plans:read",
  ESTIMATING_EQUIPMENT_PLANS_CREATE: "estimating:equipment_plans:create",
  ESTIMATING_EQUIPMENT_PLANS_UPDATE: "estimating:equipment_plans:update",
  ESTIMATING_EQUIPMENT_PLANS_APPROVE: "estimating:equipment_plans:approve",
  ESTIMATING_COST_ITEMS_READ: "estimating:cost_items:read",
  ESTIMATING_COST_ITEMS_CREATE: "estimating:cost_items:create",
  ESTIMATING_COST_ITEMS_UPDATE: "estimating:cost_items:update",
  ESTIMATING_COST_ITEMS_DELETE: "estimating:cost_items:delete",
  ESTIMATING_COST_ITEMS_EXECUTE: "estimating:cost_items:execute",
  ESTIMATING_COST_ITEMS_EXPORT: "estimating:cost_items:export",
  ESTIMATING_ASSEMBLIES_READ: "estimating:assemblies:read",
  ESTIMATING_ASSEMBLIES_CREATE: "estimating:assemblies:create",
  ESTIMATING_ASSEMBLIES_UPDATE: "estimating:assemblies:update",
  ESTIMATING_ASSEMBLIES_DELETE: "estimating:assemblies:delete",
  ESTIMATING_ESTIMATES_READ: "estimating:estimates:read",
  ESTIMATING_ESTIMATES_CREATE: "estimating:estimates:create",
  ESTIMATING_ESTIMATES_UPDATE: "estimating:estimates:update",
  ESTIMATING_ESTIMATES_DELETE: "estimating:estimates:delete",
  ESTIMATING_ESTIMATES_EXECUTE: "estimating:estimates:execute",
  ESTIMATING_ESTIMATES_EXPORT: "estimating:estimates:export",
  ESTIMATING_LINE_ITEMS_READ: "estimating:line_items:read",
  ESTIMATING_LINE_ITEMS_CREATE: "estimating:line_items:create",
  ESTIMATING_LINE_ITEMS_UPDATE: "estimating:line_items:update",
  ESTIMATING_LINE_ITEMS_DELETE: "estimating:line_items:delete",
  // M05 estimating II: sub-quotes, the approval gate, and win/loss analytics.
  ESTIMATING_QUOTE_REQUESTS_READ: "estimating:quote_requests:read",
  ESTIMATING_QUOTE_REQUESTS_CREATE: "estimating:quote_requests:create",
  ESTIMATING_QUOTE_REQUESTS_UPDATE: "estimating:quote_requests:update",
  ESTIMATING_QUOTE_REQUESTS_DELETE: "estimating:quote_requests:delete",
  ESTIMATING_QUOTE_REQUESTS_EXECUTE: "estimating:quote_requests:execute",
  ESTIMATING_QUOTE_REQUESTS_EXPORT: "estimating:quote_requests:export",
  ESTIMATING_APPROVALS_READ: "estimating:approvals:read",
  ESTIMATING_APPROVALS_CREATE: "estimating:approvals:create",
  ESTIMATING_APPROVALS_UPDATE: "estimating:approvals:update",
  ESTIMATING_ANALYTICS_READ: "estimating:analytics:read",
  /**
   * The human approval gate. Held only by the Chief Estimator persona and the
   * platform/executive roles — `submit` is rejected server-side without an
   * APPROVED decision from a holder of this permission.
   */
  ESTIMATING_ESTIMATES_APPROVE: "estimating:estimates:approve",
  /**
   * M11 client portal — INTERNAL permissions only.
   *
   * External portal contacts have NO RBAC at all: their entire authorization
   * model is the `PortalContactProjectAccess` table, checked by
   * `PortalSessionGuard`. Nothing below is ever granted to, or evaluated for,
   * a portal contact. These keys gate what STAFF may do to the portal:
   * `portal:contacts:*` is who can put an outsider inside the tenant,
   * `portal:document_shares:create` is what leaves the building, and
   * `portal:selection_items:execute` is recording a client's money decision on
   * their behalf.
   */
  PORTAL_CONTACTS_READ: "portal:contacts:read",
  PORTAL_CONTACTS_CREATE: "portal:contacts:create",
  PORTAL_CONTACTS_UPDATE: "portal:contacts:update",
  PORTAL_CONTACTS_DELETE: "portal:contacts:delete",
  PORTAL_CONTACTS_EXECUTE: "portal:contacts:execute",
  PORTAL_SELECTION_ITEMS_READ: "portal:selection_items:read",
  PORTAL_SELECTION_ITEMS_CREATE: "portal:selection_items:create",
  PORTAL_SELECTION_ITEMS_UPDATE: "portal:selection_items:update",
  PORTAL_SELECTION_ITEMS_DELETE: "portal:selection_items:delete",
  PORTAL_SELECTION_ITEMS_EXECUTE: "portal:selection_items:execute",
  PORTAL_DOCUMENT_SHARES_READ: "portal:document_shares:read",
  PORTAL_DOCUMENT_SHARES_CREATE: "portal:document_shares:create",
  PORTAL_DOCUMENT_SHARES_DELETE: "portal:document_shares:delete",
  PORTAL_MESSAGES_READ: "portal:messages:read",
  PORTAL_MESSAGES_CREATE: "portal:messages:create",
  /**
   * M12 portfolio. A showcase is a public credibility artifact, so the verbs
   * are deliberately split: `update` edits a draft, `execute` runs the
   * narrative/gallery/publish actions, and `export` renders the PDF that leaves
   * the building. `portfolio:showcases:execute` is the key that can put the
   * company's name and a client's name onto a document sent to a prequalifier.
   */
  PORTFOLIO_SHOWCASES_READ: "portfolio:showcases:read",
  PORTFOLIO_SHOWCASES_CREATE: "portfolio:showcases:create",
  PORTFOLIO_SHOWCASES_UPDATE: "portfolio:showcases:update",
  PORTFOLIO_SHOWCASES_DELETE: "portfolio:showcases:delete",
  PORTFOLIO_SHOWCASES_EXECUTE: "portfolio:showcases:execute",
  PORTFOLIO_SHOWCASES_EXPORT: "portfolio:showcases:export",
  /**
   * M13 accounting sync. `connections:execute` is the key that can connect the
   * company's books to a third party, disconnect them, or spend an OAuth grant;
   * it is deliberately separate from `update`, which only renames a connection.
   *
   * `entity_links` has no `execute`: a link is either decided by a human
   * (create/update) or removed (delete). There is no "run" verb for a mapping,
   * because a mapping that resolves itself is exactly the failure mode the
   * CONFLICT status exists to prevent.
   *
   * `job_cost_exports:read` is split out from the sync keys on purpose. It is
   * the report the client lost when they left Sage, and a project manager who
   * has no business touching the accounting connection still needs to print it.
   */
  ACCOUNTING_CONNECTIONS_READ: "accounting:connections:read",
  ACCOUNTING_CONNECTIONS_CREATE: "accounting:connections:create",
  ACCOUNTING_CONNECTIONS_UPDATE: "accounting:connections:update",
  ACCOUNTING_CONNECTIONS_DELETE: "accounting:connections:delete",
  ACCOUNTING_CONNECTIONS_EXECUTE: "accounting:connections:execute",
  ACCOUNTING_ENTITY_LINKS_READ: "accounting:entity_links:read",
  ACCOUNTING_ENTITY_LINKS_CREATE: "accounting:entity_links:create",
  ACCOUNTING_ENTITY_LINKS_UPDATE: "accounting:entity_links:update",
  ACCOUNTING_ENTITY_LINKS_DELETE: "accounting:entity_links:delete",
  ACCOUNTING_SYNC_RUNS_READ: "accounting:sync_runs:read",
  ACCOUNTING_SYNC_RUNS_EXECUTE: "accounting:sync_runs:execute",
  ACCOUNTING_JOB_COST_EXPORTS_READ: "accounting:job_cost_exports:read",
  ACCOUNTING_JOB_COST_EXPORTS_EXPORT: "accounting:job_cost_exports:export",
  /**
   * M13 payroll hours. These gate an HOURS export, not a payroll engine: there
   * is no tax calculation, no filing, and no payment behind any of them. See
   * docs/implementation_decisions.md (M13) for why that boundary is deliberate.
   *
   * `export` renders the file that leaves the building (CSV, and WH-347 where
   * certified payroll applies); `execute` runs regenerate / mark-delivered /
   * void on an existing period.
   */
  PAYROLL_EXPORTS_READ: "payroll:exports:read",
  PAYROLL_EXPORTS_CREATE: "payroll:exports:create",
  PAYROLL_EXPORTS_UPDATE: "payroll:exports:update",
  PAYROLL_EXPORTS_DELETE: "payroll:exports:delete",
  PAYROLL_EXPORTS_EXECUTE: "payroll:exports:execute",
  PAYROLL_EXPORTS_EXPORT: "payroll:exports:export",
  /**
   * P5 prevailing wage and certified payroll. Deliberately in the `payroll`
   * domain rather than a domain of their own, so they inherit M13's boundary
   * exactly: `payroll:exports:read` still rides the broad read bucket, while
   * `payroll:wage_schedules:read` is excluded from that bucket (Field Worker is
   * N) and granted with the sensitive-finance read role list. Write actions are
   * granted only to PAYROLL_WRITE_ROLES in `prisma/seed.ts`. Certified payroll
   * IS payroll; a separate domain would have created a second, quietly
   * different answer to "who may touch what a crew is paid from".
   *
   * The split between the two resources is the split between a reference table
   * and a filing. `wage_schedules:*` maintains the published rates — changing
   * one silently re-judges every payroll compared against it, which is why it
   * is a write key and not a read-only import. `certified_payroll:execute` is
   * the act of SUBMITTING a filing: it puts a named person's signature on a
   * statement of compliance, and it is the only key here that carries personal
   * liability. It is separate from `update`, which only edits a draft.
   */
  PAYROLL_WAGE_SCHEDULES_READ: "payroll:wage_schedules:read",
  PAYROLL_RESULTS_READ: "payroll:results:read",
  PAYROLL_RESULTS_IMPORT: "payroll:results:import",
  PAYROLL_RESULTS_REVIEW: "payroll:results:review",
  PAYROLL_WAGE_SCHEDULES_REVIEW: "payroll:wage_schedules:review",
  PAYROLL_WAGE_SCHEDULES_PUBLISH: "payroll:wage_schedules:publish",
  PAYROLL_CERTIFIED_PAYROLL_REVIEW: "payroll:certified_payroll:review",
  PAYROLL_WAGE_SCHEDULES_CREATE: "payroll:wage_schedules:create",
  PAYROLL_WAGE_SCHEDULES_UPDATE: "payroll:wage_schedules:update",
  PAYROLL_WAGE_SCHEDULES_DELETE: "payroll:wage_schedules:delete",
  PAYROLL_CERTIFIED_PAYROLL_READ: "payroll:certified_payroll:read",
  PAYROLL_CERTIFIED_PAYROLL_CREATE: "payroll:certified_payroll:create",
  PAYROLL_CERTIFIED_PAYROLL_UPDATE: "payroll:certified_payroll:update",
  PAYROLL_CERTIFIED_PAYROLL_EXECUTE: "payroll:certified_payroll:execute",
  PAYROLL_CERTIFIED_PAYROLL_EXPORT: "payroll:certified_payroll:export",
  /**
   * M14 banking. These gate the company's CASH — every dollar in and out of
   * every account, with the counterparty named on each line.
   *
   * `banking:*:read` is deliberately NOT in the broad read bucket the way most
   * `read` keys are. Payroll runs through the operating account: a bank register
   * tells anyone who can see it what every colleague is paid and who the owner
   * writes personal cheques to. It is granted per role, like the M11 portal
   * keys, and for the same reason.
   *
   * `connections:execute` is the key that links a real bank account, runs a
   * sync, or disconnects one; it is separate from `update`, which only renames a
   * connection or labels an account's purpose.
   *
   * Reconciliation splits three ways because they are three different acts.
   * `reconciliation:execute` RUNS the matcher, which only ever writes proposals.
   * `reconciliation:update` CONFIRMS or REJECTS one — the human decision that
   * turns a proposal into a posted fact, and the only one that can attribute
   * spend to a job. A matcher that could confirm its own proposals is the exact
   * failure this split exists to prevent.
   */
  BANKING_CONNECTIONS_READ: "banking:connections:read",
  BANKING_CONNECTIONS_CREATE: "banking:connections:create",
  BANKING_CONNECTIONS_UPDATE: "banking:connections:update",
  BANKING_CONNECTIONS_DELETE: "banking:connections:delete",
  BANKING_CONNECTIONS_EXECUTE: "banking:connections:execute",
  BANKING_ACCOUNTS_READ: "banking:accounts:read",
  BANKING_ACCOUNTS_UPDATE: "banking:accounts:update",
  BANKING_TRANSACTIONS_READ: "banking:transactions:read",
  BANKING_RECONCILIATION_READ: "banking:reconciliation:read",
  BANKING_RECONCILIATION_EXECUTE: "banking:reconciliation:execute",
  BANKING_RECONCILIATION_UPDATE: "banking:reconciliation:update",
  BANKING_SYNC_RUNS_READ: "banking:sync_runs:read",
  /**
   * M16 takeoff. These gate the engine that turns a drawing set into a
   * materials punch list — the first stage of the estimating pipeline.
   *
   * `runs:execute` RUNS the extractor, which only ever writes measurements and
   * gaps. `items:update` is the human act of CONFIRMING a measurement or
   * resolving a gap by hand, and it is a separate key for the same reason the
   * M14 reconciliation keys are split: an engine that could confirm its own
   * output would make the confirmation worthless.
   *
   * `punch_lists:approve` is deliberately its own key rather than part of
   * `update`. It is the chief estimator's signature that the list is what the
   * job actually needs — at an eleven-person GC that is one specific person
   * whose judgment the tool exists to serve, not to replace. Held by the same
   * roles that hold `estimating:estimates:approve`.
   *
   * There is no `takeoff:items:create`, and its absence is a control: a takeoff
   * item is produced by measurement or it is not produced at all. The only way
   * a human number enters the system is by resolving a GAP through
   * `items:update`, which records who did it and stamps the line MANUAL_ENTRY.
   */
  TAKEOFF_DRAWING_SETS_READ: "takeoff:drawing_sets:read",
  TAKEOFF_DRAWING_SETS_CREATE: "takeoff:drawing_sets:create",
  TAKEOFF_DRAWING_SETS_UPDATE: "takeoff:drawing_sets:update",
  TAKEOFF_DRAWING_SETS_DELETE: "takeoff:drawing_sets:delete",
  TAKEOFF_RUNS_READ: "takeoff:runs:read",
  TAKEOFF_RUNS_EXECUTE: "takeoff:runs:execute",
  TAKEOFF_ITEMS_READ: "takeoff:items:read",
  TAKEOFF_ITEMS_UPDATE: "takeoff:items:update",
  TAKEOFF_PUNCH_LISTS_READ: "takeoff:punch_lists:read",
  TAKEOFF_PUNCH_LISTS_UPDATE: "takeoff:punch_lists:update",
  TAKEOFF_PUNCH_LISTS_APPROVE: "takeoff:punch_lists:approve",
  /**
   * M17 stage one — vendor comping. The second stage of the estimating
   * pipeline: M16's punch list, priced.
   *
   * `runs:execute` runs the comparison, which only ever writes candidates,
   * scores and refusals. `lines:update` is the human act of OVERRIDING the
   * engine's pick, split from execute for the same reason M16 splits
   * `items:update` from `runs:execute`: an engine that could ratify its own
   * selection would make the ratification worthless. Every override records the
   * person and their reason.
   *
   * `runs:approve` is the chief estimator's signature on the vendor selections,
   * held by the same short list that holds `takeoff:punch_lists:approve`.
   *
   * `quote_lines:create` is what records a vendor's per-line prices against an
   * M05 quote request. It is a create key rather than part of `lines:update`
   * because entering a vendor's numbers and choosing between vendors are
   * different acts by, usually, different people.
   */
  COMPING_RUNS_READ: "comping:runs:read",
  COMPING_RUNS_EXECUTE: "comping:runs:execute",
  COMPING_RUNS_UPDATE: "comping:runs:update",
  COMPING_RUNS_APPROVE: "comping:runs:approve",
  COMPING_LINES_READ: "comping:lines:read",
  COMPING_LINES_UPDATE: "comping:lines:update",
  COMPING_QUOTE_LINES_CREATE: "comping:quote_lines:create",
  /**
   * M17 stage two — the labour stage.
   *
   * `plans:execute` derives hours from production rates and proposes a crew;
   * `tasks:update` is the human act of adding or striking a named productivity
   * factor, or changing an assignment. `plans:approve` is the signature.
   *
   * `workers:read` gates the crew roster with its skills, certifications and
   * commitments. It is separate from the plan keys because it is a view of
   * PEOPLE — who holds which ticket, and when it lapses — and that is a
   * narrower audience than "who may price a job". There is deliberately no
   * `workers:delete`: a worker's certification history is a safety record.
   */
  LABOR_PLANS_READ: "labor:plans:read",
  LABOR_PLANS_EXECUTE: "labor:plans:execute",
  LABOR_PLANS_UPDATE: "labor:plans:update",
  LABOR_PLANS_APPROVE: "labor:plans:approve",
  LABOR_TASKS_READ: "labor:tasks:read",
  LABOR_TASKS_UPDATE: "labor:tasks:update",
  LABOR_WORKERS_READ: "labor:workers:read",
  LABOR_WORKERS_UPDATE: "labor:workers:update",
  LABOR_RATES_READ: "labor:rates:read",
  LABOR_RATES_UPDATE: "labor:rates:update",
  /** Explicit observed planning sources; separate from plan/review authority. */
  RESOURCES_EMPLOYEE_CERTIFICATIONS_READ: "resources:employee_certifications:read",
  RESOURCES_EMPLOYEE_CERTIFICATIONS_CREATE: "resources:employee_certifications:create",
  RESOURCES_EQUIPMENT_READ: "resources:equipment:read",
  RESOURCES_EQUIPMENT_CREATE: "resources:equipment:create",
  RESOURCES_EQUIPMENT_MAINTENANCE_READ: "resources:equipment_maintenance:read",
  RESOURCES_EQUIPMENT_MAINTENANCE_CREATE: "resources:equipment_maintenance:create",
  RESOURCES_EQUIPMENT_MAINTENANCE_UPDATE: "resources:equipment_maintenance:update",
  /**
   * M18 — bid assembly and per-counterparty pattern sharpening. The last stage
   * of the estimating pipeline: M17's priced material and estimated labour, plus
   * the fee stack, resolved into three bids to choose between.
   *
   * `assemblies:execute` builds the stack and solves the three scenarios. It is
   * split from `assemblies:update` for the reason every stage in this pipeline
   * splits them: an engine that could ratify its own output would make the
   * ratification worthless.
   *
   * `assemblies:approve` is the bid decision itself, and it is the narrowest key
   * in the module. It mirrors `estimating:estimates:approve` exactly, because it
   * is the same signature by the same person — at Meyer, a semi-retired chief
   * estimator whose judgement this pipeline exists to inform rather than to
   * replace. The platform recommends; it never submits.
   *
   * `patterns:read` gates the counterparty bid history. It is separate from the
   * assembly keys because it is a view of COMMERCIAL HISTORY — what this company
   * has bid to whom, and how close it came — which is a narrower audience than
   * "who may price a job".
   */
  BIDDING_ASSEMBLIES_READ: "bidding:assemblies:read",
  BIDDING_ASSEMBLIES_EXECUTE: "bidding:assemblies:execute",
  BIDDING_ASSEMBLIES_UPDATE: "bidding:assemblies:update",
  BIDDING_ASSEMBLIES_APPROVE: "bidding:assemblies:approve",
  BIDDING_PATTERNS_READ: "bidding:patterns:read",
  BIDDING_PATTERNS_EXECUTE: "bidding:patterns:execute",
  /**
   * P4 — bid intake. The front door of the estimating pipeline: an inbound
   * mailbox becomes a parsed, scored, screenable list of opportunities.
   *
   * `invitations:read` is the queue and the provenance drill — what arrived,
   * what was read out of it, from which line of which message, and how sure the
   * parser was.
   *
   * `invitations:execute` is the screening decision: accept an invitation into
   * the M04 funnel, or decline it with a reason. It is split from `read` for the
   * same reason every stage of this pipeline splits them — deciding which work
   * the company chases is a commercial judgement, not a side effect of looking
   * at a list.
   *
   * There is deliberately no `create` and no `delete`. An invitation is created
   * by ingestion and is never removed: a deleted invitation is indistinguishable
   * from mail the platform never received, which would quietly corrupt the one
   * statistic this module exists to produce — how much of the inbound stream was
   * worth reading at all.
   */
  BID_INTAKE_INVITATIONS_READ: "bid_intake:invitations:read",
  BID_INTAKE_INVITATIONS_EXECUTE: "bid_intake:invitations:execute",
  /**
   * P6 — the estimating pipeline orchestrator. One run drives M16 takeoff, the
   * punch list, M17 comping and labour, and M18 assembly and sharpening,
   * without a person opening five screens in order.
   *
   * `pipeline:read` is the run screen: the stage tracker, what each stage
   * produced, and the list of lines nobody has an answer for yet. It is also
   * the key the realtime catalogue derives for the `estimating.pipeline` topic,
   * so it is what gates live progress on the socket.
   *
   * `pipeline:execute` starts and resumes a run. It is split from `read` the
   * way every stage of this pipeline splits them, and it is deliberately NOT a
   * signature: the pipeline ends at a recommended bid, and
   * `bidding:assemblies:approve` remains the only key that can sign one. A role
   * that may run the whole thing still cannot approve what comes out.
   *
   * There is no `create`, `update` or `delete`. A run is created by executing
   * one, its stages are written by the engines they call, and it is never
   * removed: a deleted run is indistinguishable from a pipeline that was never
   * run, which is exactly the evidence an estimator needs when a bid is
   * questioned six months later.
   */
  ESTIMATING_PIPELINE_READ: "estimating:pipeline:read",
  ESTIMATING_PIPELINE_EXECUTE: "estimating:pipeline:execute",
  /**
   * Client Launch Center / Implementation Workspace.
   *
   * `client_launch:workspace:read` opens the tenant's private implementation
   * workspace (overview, inventory, documents, questions, tasks, readiness).
   * It is intentionally NOT on the platform-wide read grant — a coworker in a
   * different tenant must never see another client's spend, contracts, or
   * documents by holding a generic read badge.
   *
   * `client_launch:workspace:update` covers client confirmations, answers,
   * uploads, and task updates. `client_launch:workspace:manage` is the Helios
   * control-room surface (assignments, dispositions, conflict resolution,
   * readiness overrides). `client_launch:workspace:export` covers inventory
   * and readiness exports. `client_launch:imports:execute` starts and confirms
   * import/extraction jobs.
   */
  CLIENT_LAUNCH_READ: "client_launch:workspace:read",
  CLIENT_LAUNCH_UPDATE: "client_launch:workspace:update",
  CLIENT_LAUNCH_MANAGE: "client_launch:workspace:manage",
  CLIENT_LAUNCH_EXPORT: "client_launch:workspace:export",
  CLIENT_LAUNCH_IMPORT_EXECUTE: "client_launch:imports:execute",
  /**
   * M18 HR — the employment lifecycle.
   *
   * `employees:read` is the roster: who works here, in what role, reporting to
   * whom. It is deliberately the widest grant in this block, because a
   * superintendent needs the roster and has no business seeing pay.
   *
   * `compensation:read` and `sensitive:read` are split out and NOT implied by
   * `employees:read`. Compensation is what someone is paid; sensitive is SSN
   * and date of birth. An HR coordinator who schedules reviews and chases
   * onboarding documents needs neither, and the split is what lets that role
   * exist at all. `sensitive:read` is additionally audited on the record
   * itself (EmploymentSensitiveDetail.lastViewedAt/lastViewedById), so a
   * lookup leaves a trace whether or not anyone is watching the audit log.
   *
   * `onboarding:manage` covers issuing invites and approving submissions —
   * approval creates the employment record, so it is a write grant, not a
   * read one.
   */
  HR_EMPLOYEES_READ: "hr:employees:read",
  HR_EMPLOYEES_CREATE: "hr:employees:create",
  HR_EMPLOYEES_UPDATE: "hr:employees:update",
  HR_EMPLOYEES_TERMINATE: "hr:employees:terminate",
  HR_COMPENSATION_READ: "hr:compensation:read",
  HR_COMPENSATION_UPDATE: "hr:compensation:update",
  HR_SENSITIVE_READ: "hr:sensitive:read",
  HR_SENSITIVE_UPDATE: "hr:sensitive:update",
  HR_ONBOARDING_READ: "hr:onboarding:read",
  HR_ONBOARDING_MANAGE: "hr:onboarding:manage",
  HR_DOCUMENTS_READ: "hr:documents:read",
  HR_DOCUMENTS_MANAGE: "hr:documents:manage",
  /**
   * People Imports: QBO roster ingest, HR mailbox status, document intake
   * queue. Manage covers mailbox settings, bulk upload, and Approve/Edit/Reject.
   * Applying SSN/bank/pay still requires `hr:sensitive:update`.
   */
  HR_IMPORTS_READ: "hr:imports:read",
  HR_IMPORTS_MANAGE: "hr:imports:manage",
  HR_REVIEWS_READ: "hr:reviews:read",
  HR_REVIEWS_MANAGE: "hr:reviews:manage",
  // HR-side hours. `manage` records/edits an entry; `approve` is the separate
  // checker step that moves it to APPROVED/REJECTED (maker ≠ checker).
  HR_TIME_ENTRIES_READ: "hr:time_entries:read",
  HR_TIME_ENTRIES_MANAGE: "hr:time_entries:manage",
  HR_TIME_ENTRIES_APPROVE: "hr:time_entries:approve",
  /**
   * P9 — competitor intelligence. A small, honest competitor directory and
   * real win/loss tagging against real Lead records; no derived score, no
   * trend chart, no seeded companies.
   *
   * `competitor_intel:competitors:*` covers the directory entries.
   * `competitor_intel:outcomes:*` covers tagging a Lead won/lost against one.
   * Both ride the same shape because there is exactly one actor here — the
   * estimator or principal logging a real result — not the multi-persona
   * split other domains need.
   */
  /**
   * P12 Insurance — the company's OWN program (policies, claims, premiums,
   * carriers). Deliberately a different domain from
   * `compliance:insurance_certificates:*`, which is a SUBCONTRACTOR's
   * certificate on the compliance board: one answers "what are we covered
   * for", the other "may this sub work here".
   *
   * `claims` is split from `policies` because filing and settling a claim is
   * a different job from maintaining the schedule of cover, and the amounts
   * on it are money the company is owed.
   */
  INSURANCE_POLICIES_READ: "insurance:policies:read",
  INSURANCE_POLICIES_CREATE: "insurance:policies:create",
  INSURANCE_POLICIES_UPDATE: "insurance:policies:update",
  INSURANCE_POLICIES_DELETE: "insurance:policies:delete",
  INSURANCE_CLAIMS_READ: "insurance:claims:read",
  INSURANCE_CLAIMS_CREATE: "insurance:claims:create",
  INSURANCE_CLAIMS_UPDATE: "insurance:claims:update",
  INSURANCE_CLAIMS_DELETE: "insurance:claims:delete",
  /** Premium instalments — the payment schedule and what has been paid. */
  INSURANCE_PREMIUMS_READ: "insurance:premiums:read",
  INSURANCE_PREMIUMS_MANAGE: "insurance:premiums:manage",
  /**
   * P15 Marketing — owned-channel content with a human approval gate.
   *
   * - `content:read|create|update` — draft, edit, claim-check and submit.
   *   Autopilot only produces drafts, so it rides `content:create`.
   * - `content:approve` — brand review, approve and reject. Deliberately a
   *   separate grant so an author can never one-click their own post live;
   *   the service additionally refuses an approver who submitted the item.
   * - `publications:execute` — publish approved content, retry and
   *   reconcile blocked provider jobs.
   * - `channels:manage` — connect/disconnect Meta and pick the Page.
   * - `engagement:read|reply` — the Page/Instagram command center and
   *   comment replies (which post publicly as the business).
   */
  MARKETING_CONTENT_READ: "marketing:content:read",
  MARKETING_CONTENT_CREATE: "marketing:content:create",
  MARKETING_CONTENT_UPDATE: "marketing:content:update",
  MARKETING_CONTENT_APPROVE: "marketing:content:approve",
  MARKETING_PUBLICATIONS_EXECUTE: "marketing:publications:execute",
  MARKETING_CHANNELS_MANAGE: "marketing:channels:manage",
  MARKETING_ENGAGEMENT_READ: "marketing:engagement:read",
  MARKETING_ENGAGEMENT_REPLY: "marketing:engagement:reply",
  /**
   * P12 Sales — the business-development pipeline that sits BEFORE the
   * estimating funnel. `prospects` covers prospects, touchpoints, tasks and
   * introductions; the split from `estimating:leads:*` is deliberate, because
   * a relationship with an owner is not yet an invitation to bid.
   */
  SALES_PROSPECTS_READ: "sales:prospects:read",
  SALES_PROSPECTS_CREATE: "sales:prospects:create",
  SALES_PROSPECTS_UPDATE: "sales:prospects:update",
  SALES_PROSPECTS_DELETE: "sales:prospects:delete",
  /**
   * P12 Debt — the company's borrowings. Read is as sensitive as banking:
   * the loan book shows what the business owes and to whom. Recording a
   * payment is its own grant because it moves the balance.
   */
  DEBT_DEBTS_READ: "debt:debts:read",
  DEBT_DEBTS_CREATE: "debt:debts:create",
  DEBT_DEBTS_UPDATE: "debt:debts:update",
  DEBT_DEBTS_DELETE: "debt:debts:delete",
  DEBT_PAYMENTS_READ: "debt:payments:read",
  DEBT_PAYMENTS_MANAGE: "debt:payments:manage",
  /**
   * P13 CRM depth — the relationship history behind a contact. Reads ride the
   * platform read bucket alongside `crm:client_contacts:read`: knowing what
   * was said to a client is core delivery work. Writing is the same grant as
   * editing the contact, with notes split out because a note is a judgement
   * about a person and is not always for every pair of eyes that may edit a
   * phone number.
   */
  CRM_INTERACTIONS_READ: "crm:interactions:read",
  CRM_INTERACTIONS_MANAGE: "crm:interactions:manage",
  CRM_CONTACT_NOTES_READ: "crm:contact_notes:read",
  CRM_CONTACT_NOTES_MANAGE: "crm:contact_notes:manage",
  /**
   * P13 Competitor Analysis — news, observed signals, sourced metrics and
   * alerts on top of the existing competitor directory. Deliberately no
   * derived threat score: every figure carries the source it came from.
   */
  COMPETITOR_INTEL_SIGNALS_READ: "competitor_intel:signals:read",
  COMPETITOR_INTEL_SIGNALS_MANAGE: "competitor_intel:signals:manage",
  COMPETITOR_INTEL_ALERTS_READ: "competitor_intel:alerts:read",
  COMPETITOR_INTEL_ALERTS_MANAGE: "competitor_intel:alerts:manage",
  COMPETITOR_INTEL_COMPETITORS_READ: "competitor_intel:competitors:read",
  COMPETITOR_INTEL_COMPETITORS_CREATE: "competitor_intel:competitors:create",
  COMPETITOR_INTEL_COMPETITORS_UPDATE: "competitor_intel:competitors:update",
  COMPETITOR_INTEL_COMPETITORS_DELETE: "competitor_intel:competitors:delete",
  COMPETITOR_INTEL_OUTCOMES_READ: "competitor_intel:outcomes:read",
  COMPETITOR_INTEL_OUTCOMES_CREATE: "competitor_intel:outcomes:create",
  COMPETITOR_INTEL_OUTCOMES_UPDATE: "competitor_intel:outcomes:update",
  COMPETITOR_INTEL_OUTCOMES_DELETE: "competitor_intel:outcomes:delete",
  /**
   * P14 Technology — the IT asset register plus the Microsoft Graph
   * read-through.
   *
   * Three resources, kept apart on purpose:
   *
   * - `assets` — hardware the company owns. Reading it tells you who holds
   *   which machine, so it is broader than `cameras` but narrower than a
   *   general directory read.
   * - `software` — subscriptions and their cost. This is a spend register;
   *   it is granted with the other cost-bearing modules, not with the
   *   general read bucket.
   * - `cameras` — the surveillance register. It names camera locations,
   *   coverage and private-network addresses, which is a physical-security
   *   map of every office and jobsite. Granted narrowly and never as part of
   *   a broad read.
   *
   * `devices:read` covers the live Intune / M365 view. It is separate because
   * it reaches an external system with the caller's delegated token and
   * returns device serials and compliance posture for the whole tenant.
   */
  TECHNOLOGY_ASSETS_READ: "technology:assets:read",
  TECHNOLOGY_ASSETS_CREATE: "technology:assets:create",
  TECHNOLOGY_ASSETS_UPDATE: "technology:assets:update",
  TECHNOLOGY_ASSETS_DELETE: "technology:assets:delete",
  TECHNOLOGY_ASSETS_ASSIGN: "technology:assets:assign",
  TECHNOLOGY_SOFTWARE_READ: "technology:software:read",
  TECHNOLOGY_SOFTWARE_CREATE: "technology:software:create",
  TECHNOLOGY_SOFTWARE_UPDATE: "technology:software:update",
  TECHNOLOGY_SOFTWARE_DELETE: "technology:software:delete",
  TECHNOLOGY_CAMERAS_READ: "technology:cameras:read",
  TECHNOLOGY_CAMERAS_CREATE: "technology:cameras:create",
  TECHNOLOGY_CAMERAS_UPDATE: "technology:cameras:update",
  TECHNOLOGY_CAMERAS_DELETE: "technology:cameras:delete",
  TECHNOLOGY_DEVICES_READ: "technology:devices:read",
  /**
   * Keys, recovery passwords and credentials attached to the register
   * (`TechSecret`). Lists only ever carry a masked value; `reveal` is the one
   * grant that returns plaintext, and every use is audit-logged. Granted by
   * default only to System Admin (via the platform wildcard) and the IT
   * administrator role — never as part of a read bucket.
   */
  TECHNOLOGY_SECRETS_REVEAL: "technology:secrets:reveal",
  /** Bulk import of an IT runbook or checklist into the register. Admin only. */
  TECHNOLOGY_IMPORT_MANAGE: "technology:import:manage",
  /**
   * Connect this tenant's own Microsoft 365 directory (app registration
   * credentials) and run the Graph sync. Reading the synced snapshot is
   * `technology:devices:read`.
   */
  TECHNOLOGY_M365_MANAGE: "technology:m365:manage",
  /**
   * Time & cost capture automation (Meyer; W0-W11). Domain `time_automation`.
   *
   * `controls` governs the per-tenant, per-workstream enable flags and the
   * dry-run/live switch. `go_live` is its own key because moving a workstream
   * from dry-run to live is the one step that lets the platform act on the
   * outside world; it is an Owner decision and is never part of `manage`.
   *
   * `monitoring` is the NY Civil Rights Law section 52-c notice register. The
   * employee-facing acknowledgment endpoints deliberately need no permission:
   * every employee must be able to read and sign their own notice, and those
   * handlers act only on the caller's own employment record.
   */
  TIME_AUTOMATION_CONTROLS_READ: "time_automation:controls:read",
  TIME_AUTOMATION_CONTROLS_MANAGE: "time_automation:controls:manage",
  TIME_AUTOMATION_CONTROLS_GO_LIVE: "time_automation:controls:go_live",
  TIME_AUTOMATION_MONITORING_READ: "time_automation:monitoring:read",
  TIME_AUTOMATION_MONITORING_MANAGE: "time_automation:monitoring:manage",
  /** `outbound` is the would-have-sent / was-sent record of every automated outbound action. */
  TIME_AUTOMATION_OUTBOUND_READ: "time_automation:outbound:read",
  /** `integrations` holds the Microsoft Graph application registration; the secret is write-only. */
  TIME_AUTOMATION_INTEGRATIONS_READ: "time_automation:integrations:read",
  TIME_AUTOMATION_INTEGRATIONS_MANAGE: "time_automation:integrations:manage",
  /** `provisioning`: read status, execute (activate / retry a job), manage (numbering and templates). */
  TIME_AUTOMATION_PROVISIONING_READ: "time_automation:provisioning:read",
  TIME_AUTOMATION_PROVISIONING_EXECUTE: "time_automation:provisioning:execute",
  TIME_AUTOMATION_PROVISIONING_MANAGE: "time_automation:provisioning:manage",
  /**
   * `field_time` (W2). read: punches, exceptions, reconciliation. manage: settings, crew defaults, Shifts
   * subscription and user mapping. split: re-classify a worker's day across trades (never one's own).
   * cutover: retire ClockShark - an Owner decision. `payroll_period:lock` locks/releases a payroll period.
   */
  TIME_AUTOMATION_FIELD_TIME_READ: "time_automation:field_time:read",
  TIME_AUTOMATION_FIELD_TIME_MANAGE: "time_automation:field_time:manage",
  TIME_AUTOMATION_FIELD_TIME_SPLIT: "time_automation:field_time:split",
  TIME_AUTOMATION_FIELD_TIME_CUTOVER: "time_automation:field_time:cutover",
  TIME_AUTOMATION_PAYROLL_PERIOD_LOCK: "time_automation:payroll_period:lock",
  /**
   * `office_time` (W3). read: the open-timesheet exception list and collector status. manage: thresholds, per-employee
   * overrides and running a collection. An employee's OWN signals and drafts need no permission: they are
   * session-scoped, and managers never see another employee's raw signals.
   */
  TIME_AUTOMATION_OFFICE_TIME_READ: "time_automation:office_time:read",
  TIME_AUTOMATION_OFFICE_TIME_MANAGE: "time_automation:office_time:manage",
  /**
   * SharePoint document store — the department libraries on the tenant's one
   * document site (Meyer: `/sites/meyer`). The platform writes with an
   * app-only identity that bypasses SharePoint permissions, so these keys are
   * what applies the site's permission matrix inside the platform. One
   * read/write pair per library; the grant sets per team are data in
   * `document-store-access.ts`, not a curated list.
   *
   * `estimating_billing:write` is the Accounts Receivable team's narrow write
   * into the Estimating library: pay applications into a job's `08 Billing`
   * folder only. `connection:manage` connects the site and its libraries.
   */
  DOCUMENT_STORE_CONNECTION_MANAGE: "document_store:connection:manage",
  DOCUMENT_STORE_ESTIMATING_READ: "document_store:estimating:read",
  DOCUMENT_STORE_ESTIMATING_WRITE: "document_store:estimating:write",
  DOCUMENT_STORE_ESTIMATING_BILLING_WRITE: "document_store:estimating_billing:write",
  DOCUMENT_STORE_OPERATIONS_READ: "document_store:operations:read",
  DOCUMENT_STORE_OPERATIONS_WRITE: "document_store:operations:write",
  DOCUMENT_STORE_ACCOUNTS_PAYABLE_READ: "document_store:accounts_payable:read",
  DOCUMENT_STORE_ACCOUNTS_PAYABLE_WRITE: "document_store:accounts_payable:write",
  DOCUMENT_STORE_ACCOUNTS_RECEIVABLE_READ: "document_store:accounts_receivable:read",
  DOCUMENT_STORE_ACCOUNTS_RECEIVABLE_WRITE: "document_store:accounts_receivable:write",
  DOCUMENT_STORE_FINANCE_READ: "document_store:finance:read",
  DOCUMENT_STORE_FINANCE_WRITE: "document_store:finance:write",
  DOCUMENT_STORE_HR_READ: "document_store:hr:read",
  DOCUMENT_STORE_HR_WRITE: "document_store:hr:write",
  DOCUMENT_STORE_CORPORATE_LEGAL_READ: "document_store:corporate_legal:read",
  DOCUMENT_STORE_CORPORATE_LEGAL_WRITE: "document_store:corporate_legal:write",
  DOCUMENT_STORE_COMPANY_READ: "document_store:company:read",
  DOCUMENT_STORE_COMPANY_WRITE: "document_store:company:write"
};
var SUPERUSER_PERMISSION = PERMISSIONS.PLATFORM_ADMIN;

// src/export-permissions.ts
var EXPORT_PERMISSION_KEYS = [
  "accounting:job_cost_exports:export",
  "client_launch:workspace:export",
  "compliance:insurance_certificates:export",
  "crm:client_accounts:export",
  "documents:assets:export",
  "documents:change_order_items:export",
  "documents:change_orders:export",
  "documents:rfi_responses:export",
  "documents:rfis:export",
  "documents:submittal_reviews:export",
  "documents:submittals:export",
  "estimating:cost_items:export",
  "estimating:estimates:export",
  "estimating:leads:export",
  "estimating:quote_requests:export",
  "field:camera_feeds:export",
  "field:daily_report_equipment:export",
  "field:daily_report_labor:export",
  "field:daily_report_photos:export",
  "field:daily_reports:export",
  "field:field_checklists:export",
  "field:field_tasks:export",
  "field:production_quantities:export",
  "field:site_feeds:export",
  "field:site_observations:export",
  "field:toolbox_talks:export",
  "finance:actual_costs:export",
  "finance:ap_invoices:export",
  "finance:ar_invoices:export",
  "finance:billing_applications:export",
  "finance:budget_line_items:export",
  "finance:cash_flow_forecasts:export",
  "finance:commitments:export",
  "finance:cost_codes:export",
  "finance:cost_to_complete_snapshots:export",
  "finance:financial_periods:export",
  "finance:forecast_items:export",
  "finance:margin_forecasts:export",
  "finance:project_budgets:export",
  "finance:retainage_records:export",
  "finance:revenue_forecasts:export",
  "finance:wip_snapshots:export",
  "payroll:certified_payroll:export",
  "payroll:exports:export",
  "portfolio:showcases:export",
  "procurement:bid_leveling_rows:export",
  "procurement:bid_package_invites:export",
  "procurement:bid_packages:export",
  "procurement:deliveries:export",
  "procurement:delivery_events:export",
  "procurement:inventory_items:export",
  "procurement:inventory_movements:export",
  "procurement:lead_time_forecasts:export",
  "procurement:material_categories:export",
  "procurement:materials:export",
  "procurement:purchase_order_lines:export",
  "procurement:purchase_orders:export",
  "procurement:subcontractor_packages:export",
  "procurement:supplier_risks:export",
  "procurement:vendor_bids:export",
  "procurement:vendor_contacts:export",
  "procurement:vendor_scorecards:export",
  "procurement:vendors:export",
  "projects:project_contacts:export",
  "projects:project_contracts:export",
  "projects:project_health_scores:export",
  "projects:project_issues:export",
  "projects:project_locations:export",
  "projects:project_notes:export",
  "projects:project_phases:export",
  "projects:project_reports:export",
  "projects:project_risks:export",
  "projects:project_sectors:export",
  "projects:project_stakeholders:export",
  "projects:project_status_history:export",
  "projects:projects:export",
  "reports:reports:export",
  "safety:inspection_findings:export",
  "safety:near_misses:export",
  "safety:osha_logs:export",
  "safety:permit_to_work_records:export",
  "safety:ppe_violations:export",
  "safety:safety_corrective_actions:export",
  "safety:safety_incidents:export",
  "safety:safety_inspections:export",
  "safety:site_safety_scores:export",
  "safety:training_records:export",
  "safety:training_requirements:export",
  "scheduling:constraint_logs:export",
  "scheduling:crew_assignments:export",
  "scheduling:critical_path_items:export",
  "scheduling:delay_events:export",
  "scheduling:inspection_calendar_items:export",
  "scheduling:lookahead_plans:export",
  "scheduling:milestones:export",
  "scheduling:recovery_plans:export",
  "scheduling:schedule_dependencies:export",
  "scheduling:schedule_tasks:export",
  "scheduling:schedule_versions:export",
  "scheduling:schedules:export",
  "scheduling:weather_impacts:export"
];

// src/roles.ts
var ROLES = {
  PLATFORM_OWNER: "Platform Owner",
  CEO: "CEO",
  CFO: "CFO",
  COO: "COO",
  PRESIDENT: "President",
  REGIONAL_EXECUTIVE: "Regional Executive",
  PROJECT_EXECUTIVE: "Project Executive",
  PROJECT_MANAGER: "Project Manager",
  ASSISTANT_PROJECT_MANAGER: "Assistant Project Manager",
  SUPERINTENDENT: "Superintendent",
  FIELD_WORKER: "Field Worker",
  SAFETY_MANAGER: "Safety Manager",
  QUALITY_MANAGER: "Quality Manager",
  PROCUREMENT_MANAGER: "Procurement Manager",
  FINANCE_MANAGER: "Finance Manager",
  CONTROLLER: "Controller",
  HR_MANAGER: "HR Manager",
  LEGAL_COUNSEL: "Legal Counsel",
  COMPLIANCE_MANAGER: "Compliance Manager",
  EQUIPMENT_MANAGER: "Equipment Manager",
  ESTIMATOR: "Estimator",
  /**
   * M05: the estimating approver. Holds the Estimator grant plus
   * `estimating:estimates:approve`; the estimate submit gate requires an
   * APPROVED decision from this role (or an executive/platform role).
   */
  CHIEF_ESTIMATOR: "Chief Estimator",
  SCHEDULER: "Scheduler",
  MARKET_INTELLIGENCE_ANALYST: "Market Intelligence Analyst",
  MA_LEAD: "M&A Lead",
  CLIENT_EXECUTIVE: "Client Executive",
  SYSTEM_ADMIN: "System Admin",
  INTEGRATION_ADMIN: "Integration Admin",
  AUDITOR: "Auditor"
};

// src/has-permission.ts
function hasPermission(granted, required) {
  return granted.includes(SUPERUSER_PERMISSION) || granted.includes(required);
}
function hasAnyPermission(granted, required) {
  if (required.length === 0) {
    return true;
  }
  if (granted.includes(SUPERUSER_PERMISSION)) {
    return true;
  }
  return required.some((permission) => granted.includes(permission));
}
function hasAllPermissions(granted, required) {
  if (granted.includes(SUPERUSER_PERMISSION)) {
    return true;
  }
  return required.every((permission) => granted.includes(permission));
}

// src/business-mail-grants.ts
function isExplicitBusinessMailGrant(key) {
  return key === PERMISSIONS.CRM_BUSINESS_MAIL_READ || key === PERMISSIONS.CRM_BUSINESS_MAIL_MANAGE;
}
function businessMailSafeRoleMirrorChanges(desired, current) {
  const desiredIds = new Set(desired.map((row) => row.permissionId));
  const currentIds = new Set(current.map((row) => row.permissionId));
  const automatic = (row) => typeof row.key === "string" && row.key.length > 0 && !isExplicitBusinessMailGrant(row.key);
  return {
    addIds: [
      ...new Set(
        desired.filter((row) => automatic(row) && !currentIds.has(row.permissionId)).map((row) => row.permissionId)
      )
    ],
    removeIds: [
      ...new Set(
        current.filter((row) => automatic(row) && !desiredIds.has(row.permissionId)).map((row) => row.permissionId)
      )
    ]
  };
}

// src/document-store-access.ts
var DOCUMENT_STORE_LIBRARIES = [
  "Estimating",
  "Operations",
  "AccountsPayable",
  "AccountsReceivable",
  "Finance",
  "HR",
  "CorporateLegal",
  "Company"
];
var DOCUMENT_STORE_LIBRARY_TITLES = {
  Estimating: "Estimating",
  Operations: "Operations",
  AccountsPayable: "Accounts Payable",
  AccountsReceivable: "Accounts Receivable",
  Finance: "Finance",
  HR: "HR",
  CorporateLegal: "Corporate & Legal",
  Company: "Company"
};
var LIBRARY_PERMISSIONS = {
  Estimating: {
    read: PERMISSIONS.DOCUMENT_STORE_ESTIMATING_READ,
    write: PERMISSIONS.DOCUMENT_STORE_ESTIMATING_WRITE
  },
  Operations: {
    read: PERMISSIONS.DOCUMENT_STORE_OPERATIONS_READ,
    write: PERMISSIONS.DOCUMENT_STORE_OPERATIONS_WRITE
  },
  AccountsPayable: {
    read: PERMISSIONS.DOCUMENT_STORE_ACCOUNTS_PAYABLE_READ,
    write: PERMISSIONS.DOCUMENT_STORE_ACCOUNTS_PAYABLE_WRITE
  },
  AccountsReceivable: {
    read: PERMISSIONS.DOCUMENT_STORE_ACCOUNTS_RECEIVABLE_READ,
    write: PERMISSIONS.DOCUMENT_STORE_ACCOUNTS_RECEIVABLE_WRITE
  },
  Finance: {
    read: PERMISSIONS.DOCUMENT_STORE_FINANCE_READ,
    write: PERMISSIONS.DOCUMENT_STORE_FINANCE_WRITE
  },
  HR: { read: PERMISSIONS.DOCUMENT_STORE_HR_READ, write: PERMISSIONS.DOCUMENT_STORE_HR_WRITE },
  CorporateLegal: {
    read: PERMISSIONS.DOCUMENT_STORE_CORPORATE_LEGAL_READ,
    write: PERMISSIONS.DOCUMENT_STORE_CORPORATE_LEGAL_WRITE
  },
  Company: {
    read: PERMISSIONS.DOCUMENT_STORE_COMPANY_READ,
    write: PERMISSIONS.DOCUMENT_STORE_COMPANY_WRITE
  }
};
var DOCUMENT_STORE_BILLING_FOLDER = "08 Billing";
function documentStoreReadPermission(library) {
  return LIBRARY_PERMISSIONS[library].read;
}
function documentStoreWritePermission(library) {
  return LIBRARY_PERMISSIONS[library].write;
}
var DOCUMENT_STORE_PERMISSION_KEYS = Object.values(PERMISSIONS).filter(
  (key) => key.startsWith("document_store:")
);
var DOCUMENT_STORE_TEAMS = [
  {
    key: "estimating",
    role: "Estimating Team",
    group: "estimating-team",
    libraries: { Estimating: "EDIT", Operations: "READ", Company: "READ" }
  },
  {
    key: "operations",
    role: "Operations Team",
    group: "operations-team",
    libraries: { Estimating: "EDIT", Operations: "EDIT", Company: "EDIT" }
  },
  {
    key: "accounts_payable",
    role: "Accounts Payable Team",
    group: "ap-team",
    libraries: { Estimating: "READ", AccountsPayable: "EDIT", Company: "READ" }
  },
  {
    key: "accounts_receivable",
    role: "Accounts Receivable Team",
    group: "ar-team",
    libraries: { Estimating: "BILLING_EDIT", AccountsReceivable: "EDIT", Company: "READ" }
  },
  {
    key: "finance",
    role: "Finance Team",
    group: "finance-team",
    libraries: {
      Estimating: "READ",
      AccountsPayable: "READ",
      AccountsReceivable: "READ",
      Finance: "EDIT",
      Company: "READ"
    }
  },
  {
    key: "hr",
    role: "HR Team",
    group: "hr-team",
    libraries: { HR: "EDIT", Company: "EDIT" }
  }
];
var DOCUMENT_STORE_OWNER_ROLE = "Document Site Owners";
var DOCUMENT_STORE_ROLE_NAMES = [
  ...DOCUMENT_STORE_TEAMS.map((team) => team.role),
  DOCUMENT_STORE_OWNER_ROLE
];
function levelGrants(library, level) {
  const keys = LIBRARY_PERMISSIONS[library];
  if (level === "EDIT") return [keys.read, keys.write];
  if (level === "BILLING_EDIT") return [keys.read, PERMISSIONS.DOCUMENT_STORE_ESTIMATING_BILLING_WRITE];
  return [keys.read];
}
function documentStoreRoleGrants(roleName) {
  if (roleName === DOCUMENT_STORE_OWNER_ROLE) {
    return DOCUMENT_STORE_LIBRARIES.flatMap((library) => levelGrants(library, "EDIT"));
  }
  const team = DOCUMENT_STORE_TEAMS.find((candidate) => candidate.role === roleName);
  if (!team) return null;
  return Object.entries(team.libraries).flatMap(([library, level]) => levelGrants(library, level));
}
function canReadDocumentStoreLibrary(permissions, library) {
  return hasPermission(permissions, LIBRARY_PERMISSIONS[library].read);
}
function canWriteDocumentStoreLibrary(permissions, library, recordSubfolder) {
  if (hasPermission(permissions, LIBRARY_PERMISSIONS[library].write)) return true;
  return library === "Estimating" && recordSubfolder === DOCUMENT_STORE_BILLING_FOLDER && hasPermission(permissions, PERMISSIONS.DOCUMENT_STORE_ESTIMATING_BILLING_WRITE);
}
function readableDocumentStoreLibraries(permissions) {
  return DOCUMENT_STORE_LIBRARIES.filter((library) => canReadDocumentStoreLibrary(permissions, library));
}

export { DOCUMENT_STORE_BILLING_FOLDER, DOCUMENT_STORE_LIBRARIES, DOCUMENT_STORE_LIBRARY_TITLES, DOCUMENT_STORE_OWNER_ROLE, DOCUMENT_STORE_PERMISSION_KEYS, DOCUMENT_STORE_ROLE_NAMES, DOCUMENT_STORE_TEAMS, EXPORT_PERMISSION_KEYS, PERMISSIONS, ROLES, SUPERUSER_PERMISSION, businessMailSafeRoleMirrorChanges, canReadDocumentStoreLibrary, canWriteDocumentStoreLibrary, documentStoreReadPermission, documentStoreRoleGrants, documentStoreWritePermission, hasAllPermissions, hasAnyPermission, hasPermission, isExplicitBusinessMailGrant, readableDocumentStoreLibraries };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map